import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { Popover } from 'react-bootstrap';
import { Formik, Form, Field } from 'formik';
import { formatDateStrWithTime, formatTimeStr, getTimezoneCode } from 'utils';
import Step3TimeList from './Step3TimeList';
import styles from './appointment_book.module.scss';
import { HOME_INSPECTION_TYPES } from '../../constants';

const Step3Time = ({
  listing,
  extAuth,
  isListingContact,
  isTimeChange,
  originalAppointment,
  appointment,
  appointmentsInfo = {},
  duration,
  autoConfirm,
  availableTimes,
  isMobile,
  isAdmin,
  isTeamAdmin,
  isListingAgent,
  invalidTimeslot,
  customDuration,
  dateNotAvailable,
  selectedTimes,
  enableOpenHouses,
  openHouses,
  openHouseTimes,
  offerDate,
  offerAnytime,
  timesOfDay,
  hiddenTimes,
  maxDuration,
  minutes,
  hours,
  currentStep,
  mobileWorkflowSteps,
  loadingTimes,
  appointmentTimes,
  showCaptcha,
  partialAutoConfirm,
  // functions
  expandTime,
  setDuration,
  setCustomDuration,
  updateState,
  selectTime,
  moveToStep,
  hasValidTime,
  getPrettyDate,
  timePassed,
  enableCustomDuration,
}) => {
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [filtMinutes, setFiltMinutes] = useState(minutes);

  const getTimeZone = () => {
    // Get timezone region (EST/EDT) from selected time or date
    const time = new Date(appointment.time || appointment.date);

    const timezone = getTimezoneCode(listing?.timezone, time);
    return timezone;
  };

  // Admins and listing agents can select a duration that exceeds the max duration configuration
  // Home inspections and appraisals may also exceed the max duration configuration
  const allowExceedMaxDuration =
    isAdmin ||
    isListingAgent ||
    [
      ...HOME_INSPECTION_TYPES,
      'Home Inspection',
      'Appraisal',
      'Photographer',
      'Pest Control',
    ].includes(appointment.type);

  // Changes to custom duration hour prevents some custom minutes selections if it would exceed max duration
  useEffect(() => {
    const newFilter = allowExceedMaxDuration
      ? minutes
      : minutes.filter(
          minute =>
            Number(customDuration.durationHour) * 60 + minute <= maxDuration,
        );
    setFiltMinutes(newFilter);
    if (!newFilter.includes(customDuration.durationMinutes)) {
      const newMinutes = newFilter[newFilter.length - 1] ?? 0;
      updateState(draft => {
        draft.customDuration.durationMinutes = newMinutes;
      });
    }
  }, [
    allowExceedMaxDuration,
    customDuration.durationHour,
    customDuration.durationMinutes,
    hours,
    maxDuration,
    minutes,
  ]);

  // for time selection
  useEffect(() => {
    if (customDuration.enabled) {
      setCustomDuration();
    } else if (appointment.time) {
      setDuration(duration);
    }
  }, [
    customDuration.durationMinutes,
    customDuration.durationHour,
    appointment.time,
    duration,
  ]);

  useEffect(() => {
    if (allowExceedMaxDuration || maxDuration > 60) {
      setShowCustomDuration(true);
    } else {
      setShowCustomDuration(false);
      enableCustomDuration(false);
    }
  }, [allowExceedMaxDuration, maxDuration]);

  const validate = values => {
    const errors = {};
    updateState(draft => {
      draft.duration = Number(values.duration);
      draft.customDuration.durationHour = Number(values.durationHours);
      draft.customDuration.durationMinutes = Number(values.durationMinutes);
    });
    return errors;
  };

  const filtHours = allowExceedMaxDuration
    ? hours
    : hours.filter(hour => hour * 60 <= maxDuration);

  const maxDurationHours = Math.floor(maxDuration / 60);
  const maxDurationMinutes = maxDuration % 60;
  const maxDurationTitle =
    maxDurationHours === 0
      ? `${maxDurationMinutes} min `
      : maxDurationMinutes === 0
        ? `${maxDurationHours} hr`
        : `${maxDurationHours} hr ${maxDurationMinutes} min`;

  const PopoverComponent = (
    <Popover
      id="popover-positioned-left"
      title={<b>Duration (max {maxDurationTitle})</b>}
    >
      <Formik
        initialValues={{
          duration,
          durationHours: customDuration.durationHour || 1,
          durationMinutes: customDuration.durationMinutes || 0,
        }}
        validate={values => validate(values)}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
      >
        {({ values }) => (
          <Form>
            {customDuration && !customDuration.enabled && (
              <div>
                <div className="radio">
                  <TimeLabel>
                    <Field
                      type="radio"
                      value={15}
                      checked={parseInt(values.duration, 10) === 15}
                      name="duration"
                      disabled={showCaptcha}
                    />
                    15 minutes
                  </TimeLabel>
                </div>
                {(allowExceedMaxDuration || maxDuration >= 30) && (
                  <div className="radio">
                    <TimeLabel isRed={maxDuration < 30}>
                      <Field
                        type="radio"
                        name="duration"
                        checked={parseInt(values.duration, 10) === 30}
                        value={30}
                        disabled={showCaptcha}
                      />
                      30 minutes
                    </TimeLabel>
                  </div>
                )}
                {(allowExceedMaxDuration || maxDuration >= 45) && (
                  <div className="radio">
                    <TimeLabel isRed={maxDuration < 45}>
                      <Field
                        type="radio"
                        name="duration"
                        checked={parseInt(values.duration, 10) === 45}
                        value={45}
                        disabled={showCaptcha}
                      />
                      45 minutes
                    </TimeLabel>
                  </div>
                )}
                {(allowExceedMaxDuration || maxDuration >= 60) && (
                  <div className="radio">
                    <TimeLabel isRed={maxDuration < 60}>
                      <Field
                        type="radio"
                        name="duration"
                        checked={parseInt(values.duration, 10) === 60}
                        value={60}
                        disabled={showCaptcha}
                      />
                      60 minutes
                    </TimeLabel>
                  </div>
                )}
              </div>
            )}

            {customDuration && customDuration.enabled && (
              <>
                <FlexDiv>
                  <label>Hours:</label>
                  <Field
                    component="select"
                    className="form-control"
                    name="durationHours"
                  >
                    {filtHours &&
                      filtHours.map(hour => (
                        <option key={hour} value={hour}>
                          {hour}
                        </option>
                      ))}
                  </Field>
                </FlexDiv>
                <FlexDiv>
                  <label>Minutes:</label>
                  <Field
                    component="select"
                    className="form-control"
                    name="durationMinutes"
                  >
                    {filtMinutes &&
                      filtMinutes.map(minute => (
                        <option key={minute} value={minute}>
                          {minute}
                        </option>
                      ))}
                  </Field>
                </FlexDiv>
              </>
            )}

            {showCustomDuration && (
              <div style={{ textAlign: 'center' }}>
                {customDuration.enabled ? (
                  <a onClick={() => enableCustomDuration(false)}>
                    Standard Duration
                  </a>
                ) : (
                  <a onClick={() => enableCustomDuration(true)}>
                    Custom Duration
                  </a>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <hr style={{ margin: '10px 0px' }} />
              <button
                onClick={() => document.body.click()}
                type="submit"
                style={{
                  fontSize: '100%',
                  fontFamily: 'inherit',
                  border: 0,
                  padding: 0,
                }}
                data-cy="done-customize-duration"
              >
                Done
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Popover>
  );

  return (
    <div
      className={`col-md-3 ${
        currentStep === mobileWorkflowSteps.selectTimeStep
          ? styles.stepActive
          : styles.stepInactive
      }`}
    >
      <hr className={`${styles.titleDivider} ${styles.titleDividerTop}`} />

      <h3 className={styles.titleBlock}>
        <span className={styles.stepNumber}>Step 3 - </span>
        <span className={styles.stepTitle}>
          {isMobile && (
            <span>
              <strong
                onClick={() => moveToStep(1)}
                style={{ color: '#3498db' }}
              >
                Date
              </strong>{' '}
              /{' '}
            </span>
          )}
          Select Time
        </span>
        {isMobile && isListingContact && isTimeChange && (
          <div className={styles.orignalTimeSubTitle}>
            <strong>Original Request:</strong>{' '}
            {formatDateStrWithTime(originalAppointment.time, listing.timezone)}
            {' - '}
            {formatTimeStr(originalAppointment.timeEnd, listing.timezone)}
          </div>
        )}
      </h3>

      <hr className={`${styles.titleDivider} ${styles.titleDividerBottom}`} />
      {appointment.date && (
        <div>Times reflect the listing&apos;s timezone ({getTimeZone()})</div>
      )}
      <hr className={`${styles.titleDivider} ${styles.titleDividerBottom}`} />

      {hasValidTime() ? (
        <div className="row">
          <div
            className={`col-sm-12 col-sm-push-0 col-xs-5 col-xs-push-7 ${styles.mobileContent}`}
          >
            {/* mobile */}
            {!customDuration.enabled && isMobile && (
              <Formik
                initialValues={{ duration }}
                validate={values => {
                  updateState(draft => {
                    draft.duration = values.duration;
                  });
                }}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                }}
              >
                {({ values }) => (
                  <Form>
                    <div style={{ marginLeft: '-22.5px', textAlign: 'center' }}>
                      <label style={{ display: 'block', textAlign: 'center' }}>
                        Duration:
                      </label>
                      <div className="radio">
                        <label>
                          <Field
                            type="radio"
                            name="duration"
                            checked={parseInt(values.duration, 10) === 15}
                            value={15}
                            disabled={showCaptcha}
                          />
                          15 minutes
                        </label>
                      </div>
                      {(isAdmin || isListingAgent || maxDuration >= 30) && (
                        <div className="radio">
                          <TimeLabel isRed={maxDuration < 30}>
                            <Field
                              type="radio"
                              name="duration"
                              checked={parseInt(values.duration, 10) === 30}
                              value={30}
                              disabled={showCaptcha}
                            />
                            30 minutes
                          </TimeLabel>
                        </div>
                      )}
                      {(isAdmin || isListingAgent || maxDuration >= 60) && (
                        <div className="radio">
                          <TimeLabel isRed={maxDuration < 60}>
                            <Field
                              type="radio"
                              name="duration"
                              checked={parseInt(values.duration, 10) === 60}
                              value={60}
                              disabled={showCaptcha}
                            />
                            60 minutes
                          </TimeLabel>
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            {!appointment.recordAppointment && (
              <div>
                {invalidTimeslot && (
                  <label className="invalid-timeslot">
                    RESTRICTED TIME SELECTED
                  </label>
                )}
                {(listing.canEdit ||
                  listing.canRead ||
                  extAuth?.coopDashboard ||
                  extAuth?.retsDashboard) &&
                  !invalidTimeslot &&
                  appointment.date &&
                  appointment.time &&
                  (autoConfirm || partialAutoConfirm) && (
                    <label className="autoconfirm-color">
                      AUTO-CONFIRM TIME SELECTED
                    </label>
                  )}
              </div>
            )}
          </div>
          <div className="col-sm-12 col-sm-pull-0 col-xs-7 col-xs-pull-5">
            {!dateNotAvailable && (
              <div
                id="timeSelectorContainer"
                className={classNames(styles.timeSelector, {
                  'transition animated': isMobile,
                  fadeOut: loadingTimes && isMobile,
                  fadeIn: !loadingTimes && isMobile,
                })}
              >
                {timesOfDay &&
                  timesOfDay.map(time => (
                    <Step3TimeList
                      key={time}
                      isAdmin={isAdmin}
                      isListingAgent={isListingAgent}
                      isTeamAdmin={isTeamAdmin}
                      appointment={appointment}
                      time={time}
                      hiddenTimes={hiddenTimes}
                      timePassed={timePassed}
                      availableTimes={availableTimes}
                      appointmentsInfo={appointmentsInfo}
                      duration={duration}
                      maxDuration={maxDuration}
                      openHouses={openHouses}
                      openHouseTimes={enableOpenHouses ? openHouseTimes : []}
                      offerDate={offerDate}
                      offerAnytime={offerAnytime}
                      selectedTimes={selectedTimes}
                      selectTime={selectTime}
                      showCaptcha={showCaptcha}
                      PopoverComponent={PopoverComponent}
                      customDuration={customDuration}
                      appointmentTimes={appointmentTimes}
                      enableCustomDuration={enableCustomDuration}
                      expandTime={expandTime}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-xs-12">
            <div
              className={`${styles.timeSelectorMessage} timeslot-unavailable`}
            >
              <h4>
                <i className="fa fa-frown-o" />
              </h4>
              <p>There are no available showing times on:</p>
              <p>{getPrettyDate(appointment.date)}</p>
              <p>Please select a different day.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FlexDiv = styled.div`
  width: 80%;
  margin: auto;
  display: flex;
  justify-content: space-between;
`;

const TimeLabel = styled.label`
  ${({ isRed }) => {
    if (isRed) {
      return `
    color: red;
    font-weight: bold;
    `;
    }
    return '';
  }};
`;

export default Step3Time;
